/**
 * @coeldery/event-reconciliation — 逐項對數（**唯一實作**：`rules §七 G1`）
 *
 * 對數鐵律：`賣出 ＋ 剩貨 ＋ 損壞 ＝ 出庫量`（全部換算成**件**再比 —— 場內逐件賣，
 * 所以 `箱 × 每箱件數 ＋ 件`；`rules §七 C2` 唯一例外出庫就係呢度）。
 * 對唔上 → **唔准確認**；`G6` 退回倉只准「未開箱」整箱（`leftoverUnits ＝ 0`）。
 */
import { DISPOSITION_RULES, isDispositionCode } from './dispositions.ts';

export interface Qty {
  cartons: number;
  units: number;
}

export const ZERO_QTY: Qty = { cartons: 0, units: 0 };

export const isCount = (v: unknown): boolean => typeof v === 'number' && Number.isInteger(v) && v >= 0;

/** 由 unknown 讀一個 `Qty`（缺欄當 0；唔合格 → `null` 交畀驗證報錯） */
export function readQty(v: unknown): Qty | null {
  if (v === null || v === undefined) return { cartons: 0, units: 0 };
  if (typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const cartons = o.cartons ?? 0;
  const units = o.units ?? 0;
  if (!isCount(cartons) || !isCount(units)) return null;
  return { cartons: cartons as number, units: units as number };
}

/** ⭐ 換算成「件」（唯一算法：`箱 × 每箱件數 ＋ 件`） */
export const toUnits = (q: Qty | null, unitsPerCarton: number): number =>
  (Number(q?.cartons) || 0) * (Number(unitsPerCarton) || 0) + (Number(q?.units) || 0);

export const hasQty = (q: Qty | null): boolean => toUnits(q, 1) > 0;

/** 逐項結算輸入（由 route 砌；數量一律箱 ＋ 件） */
export interface ReconcileLine {
  outboundLineId: string;
  itemId: string;
  batchId?: string | null;
  /** 賣出（場內逐件賣 → 准有散件） */
  sold: Qty;
  /** 剩貨 */
  leftover: Qty;
  /** 損壞（`G5` 要原因；相後補入 `evidence`） */
  damaged: Qty;
  /** 剩貨去向（`G2` 逐項必填；見 `DISPOSITION_CODES`） */
  dispositionCode?: string | null;
  /** `NEXT_ROADSHOW`：轉去邊張單 */
  targetOrderNo?: string | null;
  /** `TO_STORE`：轉去邊間店 */
  targetStoreCode?: string | null;
  note?: string | null;
}

/** 對數基準（由 route 由 `outbound_lines`（實際執到）＋ `items` 讀出） */
export interface ReconcileLineContext {
  outboundLineId: string;
  itemId: string;
  /** ⭐ 出庫量（＝ 實際執到嘅箱；**唔係**申報箱數） */
  outCartons: number;
  unitsPerCarton: number;
  /** 本單單號（`NEXT_ROADSHOW` 唔准轉返自己） */
  orderNo?: string | null;
}

export const ISSUE_CODES = [
  'line_unknown',
  'qty_invalid',
  'units_over_carton',
  'balance_mismatch',
  'disposition_missing',
  'disposition_invalid',
  'disposition_target_missing',
  'disposition_target_same',
  'disposition_units_not_allowed',
  'note_required',
] as const;
export type IssueCode = (typeof ISSUE_CODES)[number];
export interface ReconcileIssue {
  outboundLineId: string;
  code: IssueCode;
  /** 中文原文（`pickErrors()` 同一風格；UI 可以自己用 `code` 出 i18n） */
  message: string;
}

/** 逐項驗證：回問題清單（空 ＝ 合格）。空陣列**才**准確認（`G1`）。 */
export function lineErrors(line: ReconcileLine, ctx: ReconcileLineContext): ReconcileIssue[] {
  const at = (code: IssueCode, message: string): ReconcileIssue => ({
    outboundLineId: line.outboundLineId,
    code,
    message,
  });
  const errs: ReconcileIssue[] = [];
  const upc = Number(ctx.unitsPerCarton) || 0;

  const sold = readQty(line.sold);
  const left = readQty(line.leftover);
  const dmg = readQty(line.damaged);
  if (!sold || !left || !dmg) return [at('qty_invalid', '數量要係 ≥0 嘅整數（箱／件分開填）')];

  for (const [label, q] of [['賣出', sold], ['剩貨', left], ['損壞', dmg]] as const) {
    if (upc > 0 && q.units >= upc) {
      errs.push(at('units_over_carton', `${label}嘅件數要少過每箱件數（${upc}）`));
    }
  }

  const out = (Number(ctx.outCartons) || 0) * upc;
  const total = toUnits(sold, upc) + toUnits(left, upc) + toUnits(dmg, upc);
  if (total !== out) {
    errs.push(
      at(
        'balance_mismatch',
        `對唔上：賣出 ${toUnits(sold, upc)} ＋ 剩貨 ${toUnits(left, upc)} ＋ 損壞 ${toUnits(dmg, upc)}` +
          ` ＝ ${total} 件，但出庫係 ${out} 件（G1 鐵律）`,
      ),
    );
  }

  // ⭐ G2／G6：剩貨去向逐項必填（冇剩貨就唔理）
  if (hasQty(left)) {
    if (!isDispositionCode(line.dispositionCode)) {
      errs.push(at('disposition_missing', '有剩貨就一定要揀去向（G2）'));
      return errs;
    }
    const rule = DISPOSITION_RULES[line.dispositionCode];
    if (!rule.allowsUnits && left.units > 0) {
      errs.push(at('disposition_units_not_allowed', '退回物流倉只准「未開箱」整箱 —— 散件請改去其他去向（G6）'));
    }
    if (rule.target === 'order') {
      const target = String(line.targetOrderNo ?? '').trim();
      if (!target) errs.push(at('disposition_target_missing', '轉下一場要指定目標出庫單（G2）'));
      else if (ctx.orderNo && target === String(ctx.orderNo)) {
        errs.push(at('disposition_target_same', '唔可以轉返同一張單（貨冇離開過）'));
      }
    }
    if (rule.target === 'store' && !String(line.targetStoreCode ?? '').trim()) {
      errs.push(at('disposition_target_missing', '轉店要指定店代碼'));
    }
    if (rule.needsNote && !String(line.note ?? '').trim()) {
      errs.push(at('note_required', '損毀註銷要填原因（G5）'));
    }
  }
  return errs;
}

/** 全單驗證（逐項 ＋ 呢行係唔係屬於呢張單） */
export function reconcileErrors(
  lines: ReconcileLine[],
  ctxs: ReconcileLineContext[],
): ReconcileIssue[] {
  const byId = new Map(ctxs.map((c) => [c.outboundLineId, c]));
  const errs: ReconcileIssue[] = [];
  for (const line of lines) {
    const ctx = byId.get(line.outboundLineId);
    if (!ctx) {
      errs.push({
        outboundLineId: line.outboundLineId,
        code: 'line_unknown',
        message: '呢一行唔屬於呢張出庫單',
      });
      continue;
    }
    errs.push(...lineErrors(line, ctx));
  }
  return errs;
}

export interface ReconcileSummary {
  itemCount: number;
  outCartons: number;
  soldCartons: number;
  leftoverCartons: number;
  damagedCartons: number;
  soldUnits: number;
  leftoverUnits: number;
  damagedUnits: number;
  /** 對得上嘅行數 */
  balancedLines: number;
  /** 對唔上嘅行數（`G1`） */
  unbalancedLines: number;
  /** 有剩貨但未填去向嘅行數（`G2`） */
  missingDisposition: number;
  /** 有剩貨嘅行數 */
  leftoverLines: number;
  /** ⭐ 全部行都對得上（＝ 准確認） */
  balanced: boolean;
}

/** 全單摘要（前端顯示對數；route 用 `balanced` 做確認閘） */
export function reconcileSummary(
  lines: ReconcileLine[],
  ctxs: ReconcileLineContext[],
): ReconcileSummary {
  const byId = new Map(ctxs.map((c) => [c.outboundLineId, c]));
  const s: ReconcileSummary = {
    itemCount: lines.length,
    outCartons: 0,
    soldCartons: 0,
    leftoverCartons: 0,
    damagedCartons: 0,
    soldUnits: 0,
    leftoverUnits: 0,
    damagedUnits: 0,
    balancedLines: 0,
    unbalancedLines: 0,
    missingDisposition: 0,
    leftoverLines: 0,
    balanced: false,
  };
  for (const line of lines) {
    const ctx = byId.get(line.outboundLineId);
    const upc = Number(ctx?.unitsPerCarton) || 0;
    const sold = readQty(line.sold) ?? ZERO_QTY;
    const left = readQty(line.leftover) ?? ZERO_QTY;
    const dmg = readQty(line.damaged) ?? ZERO_QTY;
    s.outCartons += Number(ctx?.outCartons) || 0;
    s.soldCartons += sold.cartons;
    s.leftoverCartons += left.cartons;
    s.damagedCartons += dmg.cartons;
    s.soldUnits += toUnits(sold, upc);
    s.leftoverUnits += toUnits(left, upc);
    s.damagedUnits += toUnits(dmg, upc);
    if (hasQty(left)) {
      s.leftoverLines++;
      if (!isDispositionCode(line.dispositionCode)) s.missingDisposition++;
    }
    if (ctx && lineErrors(line, ctx).length === 0) s.balancedLines++;
    else s.unbalancedLines++;
  }
  s.balanced = lines.length > 0 && s.unbalancedLines === 0;
  return s;
}

